import { useState, useEffect, useMemo } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import tw from 'twin.macro';
import { makeStyles } from '@material-ui/core';
import FormControl from '@material-ui/core/FormControl';
import FormGroup from '@material-ui/core/FormGroup';
import { useMediaQuery } from 'react-responsive';
import clsx from 'clsx';
import { hotelSearch } from '../../../../../store/slices/hotel';
import { setHotelTripSearchInputs } from '../../../../../store/slices/tripSearch';
import Checkbox from '../../../../Checkbox';
import { checkFoldMultipleRateCodes } from '../../../../../store/slices/user';
import Filter from '../../../../Filters/Filter';
import Dropdown from '../../../../Dropdown';
import RateCodeConfigPreferences from './RateCodeConfigPreferences';
import OptionsList from '../../../../Filters/OptionsList';
import usePreferencesFilter from '../../../../../hooks/usePreferencesHook';
import CustomSearchBar from '../../../../Input/CustomInput/customSearchBar';
import ContentSourceFilter from '../ContentSourceFilter';
import useContentSourceFilter from '../../../../../hooks/useContentSourceFilter'; // Import the new hook
import useBestAvailableRatesOption from '../../../../../hooks/useBestAvailableRatesOption';

const RateCodePreferencesFilter = ({ tripId, variant, filterBgColor }) => {
  const isHotelTripSearch = !tripId;
  const hotelSearchObj = useSelector((store) =>
    !isHotelTripSearch
      ? (store.hotel.hotelSearches[tripId] || {}).searchObj || {}
      : store.tripSearch.searchInputs.hotel,
  );
  const hotelRateCodes = useSelector((store) => store.user.hotelRateCodes);
  const { travelAgency = {}, isLoggedIn } = useSelector((store) => store.user);
  const [options, setOptions] = useState([]);
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  // Use the content source filter hook
  const { doTakeHotelContentSourceInput } = useContentSourceFilter(tripId);

  const doShowAgentPreferedHotelCheckboxInRateCodesFilter =
    travelAgency &&
    travelAgency.settings &&
    travelAgency.settings.doShowAgentPreferedHotelCheckboxInRateCodesFilter;
  const agentPreferredHotelFilterLabel =
    ((travelAgency || {}).settings || {}).agentPreferedHotelFilterLabel ||
    'Only show hotels on agent-preferred hotels list';
  const hotelRateCodesLabel =
    travelAgency && travelAgency.filterSettings && travelAgency.filterSettings.hotelRateCodesLabel;
  const doReDesignRateCodesFilter =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.doRedesignRateCodesFilter;
  const doShowRateCodesSearchBar =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.doShowRateCodesSearchBar;
  const preferredPartnerRateCodeLabel =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.preferredPartnerRateCodeLabel;
  const preferredPartnerProgramsLabel =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.preferredPartnerProgramsLabel;
  const searchRateCodesPlaceholder =
    travelAgency &&
    travelAgency.filterSettings &&
    travelAgency.filterSettings.searchRateCodesPlaceholder;
  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });

  const useFormStyle = makeStyles(() => ({
    root: {
      '&': tw`flex-nowrap px-4 w-full`,
    },
  }));
  // State for filtering rate codes
  const [searchQuery, setSearchQuery] = useState('');

  // Handle rate codes search input changes
  const handleSearchChange = (searchValue) => {
    setSearchQuery(searchValue);
  };
  const isGroupedRate = checkFoldMultipleRateCodes(isLoggedIn, travelAgency);
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;

    const lowerCaseSearch = searchQuery.toLowerCase();

    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerCaseSearch) ||
        (!isGroupedRate && option.value.toLowerCase().includes(lowerCaseSearch)),
    );
  }, [isGroupedRate, options, searchQuery]);

  useEffect(() => {
    if (hotelRateCodes && hotelRateCodes.length) {
      let isOptionsSet = false;
      if (isGroupedRate) {
        // Fold rate codes by "groupName". Ignore rate codes without "group" property.
        const rateCodesWithGroup = _.filter(hotelRateCodes, (r) => r.group && r.group.groupName);
        if (rateCodesWithGroup.length) {
          const rateGroups = _.groupBy(rateCodesWithGroup, (r) => r.group.groupName);
          const getLabel = (key, rateGroup) => {
            let label = `${key}`;
            if (travelAgency.settings.doShowRateCodesFoldedIntoGroupInLabel) {
              label += ` - ${_.map(rateGroup, 'rateCode').join(', ')}`;
            }
            return label;
          };

          const rates = _.map(rateGroups, (rateGroup, key) => ({
            value: _.map(rateGroup, 'rateCode').join(','),
            label: getLabel(key, rateGroup),
            rateCodes: _.map(rateGroup, 'rateCode'),
          }));
          // Remove duplicate rate codes
          setOptions(_.uniqBy(rates, (o) => `${o.value}-${o.label}`));
          isOptionsSet = true;
        }
      }
      if (!isOptionsSet) {
        const rawOptions = _.map(hotelRateCodes, (r) => {
          let label;
          if (r.text) {
            label = travelAgency?.settings?.doShowRateCodesInLabel
              ? `${r.text} (${r.rateCode})`
              : r.text;
          } else {
            label = r.rateCode;
          }
          return {
            value: r.rateCode,
            label,
          };
        });
        setOptions(_.uniqBy(rawOptions, (o) => `${o.value}-${o.label}`));
      }
    }
  }, [hotelRateCodes, isGroupedRate, isLoggedIn, travelAgency, travelAgency.settings]);
  const label = hotelRateCodesLabel || preferredPartnerRateCodeLabel || 'Rate Codes';

  const selectedValues = useMemo(() => {
    const selectedRates = (hotelSearchObj && hotelSearchObj.rateCodes) || [];
    if (selectedRates.length && _.find(options, (o) => o.rateCodes && o.rateCodes.length)) {
      // Check folded options, when any rate code from folded rate codes selected.
      return _.map(
        _.filter(
          options,
          (o) => o.rateCodes && _.find(selectedRates, (r) => o.rateCodes.indexOf(r) > -1),
        ),
        'value',
      );
    }
    return selectedRates;
  }, [hotelSearchObj, options]);

  const { doShowSabreRateCodes, doHideRateCodePreferencesFilter } =
    useBestAvailableRatesOption(tripId);

  const {
    handlePreferencesChange,
    handlePreferencesClear,
    agentPreferencesLabel,
    preferences,
    isAgentPreferencesEmpty,
    selectedPreferences,
  } = usePreferencesFilter({ tripId, doShowInPreferences: false, doHideRateCodePreferencesFilter });

  // Label prefixes for selected dropdown items
  const selectedValuePrefix = useMemo(() => {
    const selectedValuesPrefixes = [];

    if (
      doShowAgentPreferedHotelCheckboxInRateCodesFilter &&
      hotelSearchObj &&
      hotelSearchObj.doShowOnlyAgentPreferredHotels
    ) {
      selectedValuesPrefixes.push(agentPreferredHotelFilterLabel);
    }

    if (selectedValues.length > 0) {
      selectedValuesPrefixes.push(
        `${hotelRateCodesLabel || preferredPartnerRateCodeLabel || 'Preferred partner rate code'}:`,
      );
    }

    const hasAgentPreferences =
      doReDesignRateCodesFilter && agentPreferencesLabel && !isAgentPreferencesEmpty;

    if (hasAgentPreferences && selectedValuesPrefixes.length > 0) {
      return `${agentPreferencesLabel}, ${selectedValuesPrefixes.join(', ')}`;
    }

    return hasAgentPreferences ? agentPreferencesLabel : selectedValuesPrefixes.join(', ');
  }, [
    doShowAgentPreferedHotelCheckboxInRateCodesFilter,
    hotelSearchObj,
    selectedValues.length,
    doReDesignRateCodesFilter,
    agentPreferencesLabel,
    isAgentPreferencesEmpty,
    agentPreferredHotelFilterLabel,
    hotelRateCodesLabel,
    preferredPartnerRateCodeLabel,
  ]);

  const formClasses = useFormStyle();
  const isEmpty =
    (doReDesignRateCodesFilter
      ? isAgentPreferencesEmpty && !selectedValues.length
      : !selectedValues.length) &&
    !(
      doShowAgentPreferedHotelCheckboxInRateCodesFilter &&
      hotelSearchObj &&
      hotelSearchObj.doShowOnlyAgentPreferredHotels
    );
  const getLabel = () =>
    isEmpty
      ? label
      : `${selectedValuePrefix} ${options
          .filter((o) => selectedValues.includes(o.value))
          .map((o) => o.label)
          .join(', ')}`.trim();

  const onChange = (newValue, preferredHotelFilterNewVal) => {
    const payload = {
      tripId,
      searchObj: {
        page: 1,
      },
    };
    if (preferredHotelFilterNewVal !== undefined) {
      payload.searchObj.doShowOnlyAgentPreferredHotels = preferredHotelFilterNewVal;
    } else {
      payload.searchObj.rateCodes = _.flatMap(newValue, (r) => r.split(','));
    }
    if (isHotelTripSearch) {
      dispatch(setHotelTripSearchInputs({ ...payload.searchObj }));
    } else {
      dispatch(hotelSearch(payload));
    }
    track('rateCodePreferencesFilter.handleChange', {
      filterAmenities: JSON.stringify(newValue),
    });
  };

  const handleChange = (itemValue, newValue) => {
    const newValues = newValue
      ? [...selectedValues, itemValue].filter((v, i, self) => self.indexOf(v) === i)
      : selectedValues.filter((v) => v !== itemValue);
    onChange(newValues);
  };

  const handleClear = () => {
    if (doReDesignRateCodesFilter) handlePreferencesClear();
    if (doShowAgentPreferedHotelCheckboxInRateCodesFilter) onChange(undefined, false);
    onChange([]);
  };

  const childElement = !doShowAgentPreferedHotelCheckboxInRateCodesFilter ? undefined : (
    <div className={clsx('w-full col-span-5', { 'ml-4': !doReDesignRateCodesFilter })}>
      <Checkbox
        label={agentPreferredHotelFilterLabel}
        checked={hotelSearchObj && hotelSearchObj.doShowOnlyAgentPreferredHotels}
        onChange={(event, newValue) => onChange(undefined, newValue)}
        checkboxStyles={{
          padding: '5px',
        }}
      />
    </div>
  );
  // Toggle selection for all rate codes
  const toggleAllOptionsSelection = (isSelect) => {
    const updatedValues = isSelect && options ? options.map((option) => option.value) : [];
    onChange(updatedValues);
    if (doShowAgentPreferedHotelCheckboxInRateCodesFilter) {
      onChange(undefined, isSelect);
    }
  };

  // Select a single rate code
  const selectSingleRateCode = (rateCode) => {
    onChange([rateCode]);
  };

  useEffect(() => {
    if (
      hotelRateCodes &&
      hotelRateCodes.length &&
      hotelSearchObj.rateCodes === undefined &&
      doShowSabreRateCodes
    ) {
      const filteredRateCodes = _.filter(hotelRateCodes, (r) => r.isSelectedByDefault);
      if (filteredRateCodes.length > 0)
        dispatch(setHotelTripSearchInputs({ rateCodes: _.map(filteredRateCodes, 'rateCode') }));
    }
  }, [hotelRateCodes, dispatch, hotelSearchObj.rateCodes, doShowSabreRateCodes]);

  const doShowRateCodesFilter =
    doReDesignRateCodesFilter || (!doHideRateCodePreferencesFilter && hotelRateCodes?.length > 0);

  return doShowRateCodesFilter ? (
    <Filter
      label={getLabel()}
      menuWidthClass="w-full md:w-max"
      variant={variant}
      filterMaxWidthClass={isSmallScreen ? 'w-full' : 'max-w-72'}
      isFilterEmpty={isEmpty}
      handleClear={handleClear}
      widthInside="max-w-90"
      bgClassName={!isHotelTripSearch ? '' : filterBgColor}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
      onClose={() => {
        setSearchQuery('');
      }}
    >
      <Dropdown.Item>
        <FormControl
          className="grid col-span-5 grid-cols-2 gap-y-2 w-full items-center"
          component="fieldset"
        >
          <FormGroup classes={formClasses} className={clsx('overflow-x-auto max-h-1')}>
            {doReDesignRateCodesFilter && (
              <>
                <RateCodeConfigPreferences
                  onChange={handlePreferencesChange}
                  preferences={preferences}
                  selectedPreferences={selectedPreferences}
                />
                {childElement}
                {doTakeHotelContentSourceInput && <ContentSourceFilter tripId={tripId} />}
                {doReDesignRateCodesFilter && doHideRateCodePreferencesFilter ? (
                  <></>
                ) : (
                  <div className="flex justify-between py-3 pb-2 gap-16">
                    <p className="font-extrabold text-black -ml-1">
                      {preferredPartnerProgramsLabel || 'Preferred Partner Programs'}
                    </p>
                    <div className="flex gap-2 underline">
                      <a
                        className="inline-block align-middle self-center"
                        tabIndex="-1"
                        role="button"
                        onClick={() => toggleAllOptionsSelection(true)}
                        onKeyDown={() => {}}
                      >
                        <span>Select All</span>
                      </a>
                      <a
                        className="inline-block align-middle self-center"
                        tabIndex="-1"
                        role="button"
                        onClick={() => toggleAllOptionsSelection(false)}
                        onKeyDown={() => {}}
                      >
                        <span>Deselect All</span>
                      </a>
                    </div>
                  </div>
                )}
              </>
            )}
            {options?.length > 0 &&
              doShowRateCodesSearchBar &&
              !doHideRateCodePreferencesFilter && (
                <div className="my-1 -ml-1 ">
                  <CustomSearchBar
                    label={searchRateCodesPlaceholder || 'Search preferred partner rates...'}
                    onChange={handleSearchChange}
                    doShowSearchIcon={false}
                  />
                </div>
              )}
            {!doHideRateCodePreferencesFilter && (
              <OptionsList
                options={filteredOptions}
                selectedValues={selectedValues}
                onChange={handleChange}
                selectSingleRateCode={doReDesignRateCodesFilter ? selectSingleRateCode : null}
              />
            )}
          </FormGroup>
        </FormControl>
        {!doReDesignRateCodesFilter &&
          agentPreferredHotelFilterLabel.toLowerCase().includes(searchQuery) &&
          childElement}
      </Dropdown.Item>
    </Filter>
  ) : (
    <></>
  );
};

RateCodePreferencesFilter.propTypes = {
  tripId: PropTypes.string,
  variant: PropTypes.string,
  filterBgColor: PropTypes.string,
};

RateCodePreferencesFilter.defaultProps = {
  tripId: undefined,
  variant: 'outline',
  filterBgColor: undefined,
};

export default RateCodePreferencesFilter;
